import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useAuthController } from "@firecms/core";
import { Button, Card, CircularProgress, Typography } from "@firecms/ui";
import { FirebaseError } from "firebase/app";
import {
  acceptAccountInvitation,
  getMyPendingAccountInvitations,
  type PendingAccountInvitation,
} from "../firebase/invitations";
import { getMyPermissions } from "../firebase/getMyPermissions";
import { mergeAuthExtras, type AuthExtras } from "../types/auth";
import { buildAccountPath, buildHubPath } from "../workspace/workspaceRoutes";
import {
  matchInvitationEmail,
  parseAccountInvitationSearch,
} from "./accountInvitationLink";

const muted = "text-surface-600 dark:text-surface-400";
const body = "text-surface-900 dark:text-surface-100";
const card =
  "overflow-hidden rounded-2xl border border-surface-200/90 bg-surface-50 px-6 py-8 shadow-sm dark:border-surface-700 dark:bg-surface-800 sm:px-8";
const primaryButton =
  "w-full !rounded-xl !border-0 !bg-[var(--color-primary)] !px-5 !py-2.5 !text-sm !font-semibold !normal-case !tracking-normal !text-white !shadow-md hover:!brightness-110 disabled:!cursor-not-allowed disabled:!opacity-50 sm:!w-auto";
const secondaryButton =
  "w-full !rounded-xl !normal-case sm:!w-auto";

function invitationLoadErrorMessage(error: unknown): string {
  if (error instanceof FirebaseError) {
    if (error.code === "functions/failed-precondition") {
      const detail = error.message.toLowerCase();
      if (detail.includes("email must be verified")) {
        return "Debés verificar tu correo antes de ver o aceptar invitaciones.";
      }
      if (detail.includes("user email is required")) {
        return "Tu cuenta no tiene un correo asociado. Usá Google o email/contraseña con correo.";
      }
      if (detail.includes("no longer pending")) {
        return "Esta invitación ya no está pendiente.";
      }
    }
    if (error.code === "functions/permission-denied") {
      return "Esta invitación no es para el correo con el que iniciaste sesión.";
    }
    if (error.code === "functions/not-found") {
      return "Esta invitación ya no está pendiente.";
    }
    if (error.code === "functions/unauthenticated") {
      return "La sesión expiró. Volvé a iniciar sesión.";
    }
  }

  return "No pudimos cargar la invitación. Probá de nuevo en unos segundos.";
}

export function AccountInvitationPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const authController = useAuthController();
  const search = searchParams.toString();
  const link = useMemo(() => parseAccountInvitationSearch(search), [search]);
  const authEmail = authController.user?.email;
  const emailMatch = useMemo(
    () => (link ? matchInvitationEmail(authEmail, link.email) : null),
    [authEmail, link]
  );

  const [loading, setLoading] = useState(emailMatch?.kind === "match");
  const [invitation, setInvitation] = useState<PendingAccountInvitation | null>(
    null
  );
  const [unavailable, setUnavailable] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [accepting, setAccepting] = useState(false);
  const [signingOut, setSigningOut] = useState(false);

  const loadInvitation = useCallback(async () => {
    if (!link || emailMatch?.kind !== "match") {
      return;
    }

    setLoading(true);
    setError(null);
    try {
      const pending = await getMyPendingAccountInvitations();
      const found =
        pending.find(
          (item) =>
            item.accountId === link.accountId &&
            item.invitationId.trim().toLowerCase() === link.email
        ) ?? null;
      setInvitation(found);
      setUnavailable(!found);
    } catch (loadError) {
      setInvitation(null);
      setUnavailable(false);
      setError(invitationLoadErrorMessage(loadError));
    } finally {
      setLoading(false);
    }
  }, [emailMatch?.kind, link]);

  useEffect(() => {
    void loadInvitation();
  }, [loadInvitation]);

  const handleAccept = async () => {
    if (!link || !invitation) {
      return;
    }

    setAccepting(true);
    setError(null);
    try {
      await acceptAccountInvitation(link.accountId, invitation.invitationId);
      try {
        const extras = await getMyPermissions();
        authController.setExtra(
          mergeAuthExtras(authController.extra as AuthExtras | undefined, extras)
        );
      } catch (permissionsError) {
        console.warn("getMyPermissions failed", permissionsError);
      }
      navigate(buildAccountPath(link.accountId));
    } catch (acceptError) {
      const gone =
        acceptError instanceof FirebaseError &&
        (acceptError.code === "functions/not-found" ||
          (acceptError.code === "functions/failed-precondition" &&
            acceptError.message.toLowerCase().includes("no longer pending")));
      if (gone) {
        setInvitation(null);
        setUnavailable(true);
        setError(null);
      } else {
        setError(invitationLoadErrorMessage(acceptError));
      }
    } finally {
      setAccepting(false);
    }
  };

  const handleSignOut = async () => {
    setSigningOut(true);
    setError(null);
    try {
      await authController.signOut();
    } catch (signOutError) {
      console.warn("signOut failed", signOutError);
      setError("No pudimos cerrar la sesión. Probá de nuevo.");
      setSigningOut(false);
    }
  };

  return (
    <div className="mx-auto w-full max-w-xl px-4 py-10 sm:px-6">
      <Card className={card}>
        {link && emailMatch?.kind === "match" && loading ? (
          <div className="flex flex-col items-center gap-4 py-6">
            <CircularProgress size="medium" />
            <Typography variant="body2" className={muted}>
              Buscando tu invitación…
            </Typography>
          </div>
        ) : null}

        {!link ? (
          <InviteCopy
            title="Este enlace de invitación no es válido"
            body="Pedile a quien te invitó que te envíe el correo de nuevo."
          />
        ) : null}

        {emailMatch?.kind === "missing-auth-email" ? (
          <InviteCopy
            title="Tu cuenta no tiene un correo asociado"
            body="Usá Google o email/contraseña con correo para aceptar la invitación."
          />
        ) : null}

        {emailMatch?.kind === "mismatch" ? (
          <InviteCopy
            title="Esta invitación es para otro correo"
            body={`La invitación es para ${emailMatch.invitedEmail}. Iniciaste sesión como ${emailMatch.authEmail}. Cerrá sesión y entrá con el correo de la invitación para aceptarla.`}
          />
        ) : null}

        {emailMatch?.kind === "match" && !loading && invitation ? (
          <InviteCopy
            title="Te invitaron a una cuenta"
            body={
              invitation.accountName
                ? `${invitation.inviterName ?? "Alguien"} te invitó a administrar la cuenta ${invitation.accountName}.`
                : "Tenés una invitación pendiente para administrar esta cuenta."
            }
          />
        ) : null}

        {emailMatch?.kind === "match" && !loading && unavailable ? (
          <InviteCopy
            title="Esta invitación ya no está pendiente"
            body="Puede que ya la hayas aceptado o que la hayan cancelado."
          />
        ) : null}

        {error ? (
          <Typography
            variant="body2"
            className="mt-4 leading-relaxed text-red-800 dark:text-red-200"
          >
            {error}
          </Typography>
        ) : null}

        <div className="mt-6 flex flex-col gap-3 sm:flex-row">
          {emailMatch?.kind === "match" && invitation ? (
            <Button
              variant="filled"
              size="medium"
              className={primaryButton}
              disabled={accepting}
              onClick={() => void handleAccept()}
            >
              {accepting ? "Un momento…" : "Aceptar invitación"}
            </Button>
          ) : null}

          {emailMatch?.kind === "mismatch" ||
          emailMatch?.kind === "missing-auth-email" ? (
            <Button
              variant="filled"
              size="medium"
              className={primaryButton}
              disabled={signingOut}
              onClick={() => void handleSignOut()}
            >
              {signingOut ? "Cerrando sesión…" : "Cerrar sesión"}
            </Button>
          ) : null}

          {emailMatch?.kind === "match" && error && !invitation ? (
            <Button
              variant="outlined"
              size="medium"
              className={secondaryButton}
              onClick={() => void loadInvitation()}
            >
              Reintentar
            </Button>
          ) : null}

          <Button
            variant="outlined"
            size="medium"
            className={secondaryButton}
            onClick={() => navigate(buildHubPath())}
          >
            Ir al inicio
          </Button>
        </div>
      </Card>
    </div>
  );
}

function InviteCopy({ title, body: copy }: { title: string; body: string }) {
  return (
    <>
      <Typography variant="h6" className={body}>
        {title}
      </Typography>
      <Typography variant="body2" className={`mt-2 leading-relaxed ${muted}`}>
        {copy}
      </Typography>
    </>
  );
}
